// Where a post-delivery edit was asked (WRP-045). Pure, so the canvas, the
// legacy panel, the node dialogs and the edit evidence card share one rule.
//
// Since bot #546 a web edit's events say so: `edit.requested` carries
// `channel: "web"`, `surface: "my"` (my.echoflicks.com) and `mode`
// ("direct" = picked changes, "felix" = asked in words); `edit.confirm_requested`
// / `edit.cancel_requested` carry `channel: "web"`; and a failed web edit's
// `generation.failed` carries `edit_failure: true` with the same channel and
// surface. WhatsApp edits and Studio re-cuts never say `channel: "web"`.

import type { EchoJourneyNode } from "../../types";

export type EditChannelBadge = {
  /** Chip text: the web surface (my.echoflicks.com), or "web" when unknown. */
  label: string;
  /** How the customer asked, when the event says: in words to Felix, or direct changes. */
  mode: "Felix" | "direct" | null;
  /** Tooltip sentence. */
  title: string;
};

const WEB_SURFACES: Record<string, string> = { my: "my.echoflicks.com" };

type Payload = Record<string, unknown>;

function asPayload(value: unknown): Payload | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Payload)
    : null;
}

/** The event payload a journey node was built from (bare, or under `payload`). */
function nodePayload(node: Pick<EchoJourneyNode, "raw_payload">) {
  const raw = asPayload(node.raw_payload);
  return asPayload(raw?.payload) ?? raw;
}

function editMode(value: unknown): EditChannelBadge["mode"] {
  if (value === "felix") return "Felix";
  if (value === "direct") return "direct";
  return null;
}

/** The web-channel badge for an edit event, or null for any other node. */
export function editChannelBadge(
  node: Pick<EchoJourneyNode, "type" | "raw_payload">,
): EditChannelBadge | null {
  const payload = nodePayload(node);
  if (!payload || payload.channel !== "web") return null;
  const isEdit =
    String(node.type ?? "").startsWith("edit.") ||
    payload.edit_failure === true;
  if (!isEdit) return null;
  const surface = typeof payload.surface === "string" ? payload.surface : "";
  const label = WEB_SURFACES[surface] ?? "web";
  const mode = editMode(payload.mode);
  const where = label === "web" ? "the web" : label;
  const how =
    mode === "Felix"
      ? ", in words to Felix"
      : mode === "direct"
        ? ", as direct changes"
        : "";
  return { label, mode, title: `Edit asked on ${where}${how}` };
}

/**
 * The newest web-channel badge among a journey's edit events: how the edit on
 * this page was asked. Prefers `edit.requested`, which alone carries the mode.
 */
export function journeyEditChannel(
  nodes: Array<
    Pick<EchoJourneyNode, "type" | "raw_payload" | "occurred_at">
  > = [],
): EditChannelBadge | null {
  const sorted = [...nodes].sort((a, b) =>
    (b.occurred_at ?? "").localeCompare(a.occurred_at ?? ""),
  );
  let fallback: EditChannelBadge | null = null;
  for (const node of sorted) {
    const badge = editChannelBadge(node);
    if (!badge) continue;
    if (node.type === "edit.requested") return badge;
    fallback ??= badge;
  }
  return fallback;
}
